import React, {
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  KoraxPage,
  KoraxPageHeader
} from "../../components/KoraxUI";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "auto",
    background:
      theme.palette.type === "dark"
        ? "#080E1C"
        : "#F8FAFC",
    fontFamily: "'Inter', system-ui, sans-serif",
    ...theme.scrollbarStyles
  },

  inner: {
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 18
  },

  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap"
  },

  button: {
    minHeight: 38,
    padding: "8px 14px",
    borderRadius: 10,
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.07)",
    color: "#1D4ED8",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 800,
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed"
    }
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14
  },

  summaryCard: {
    padding: 17,
    borderRadius: 16,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow: "0 4px 18px rgba(15,23,42,.045)"
  },

  summaryLabel: {
    color: "#94A3B8",
    fontSize: "0.67rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".04em"
  },

  summaryValue: {
    marginTop: 8,
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "1.35rem",
    fontWeight: 900
  },

  card: {
    minWidth: 0,
    padding: 20,
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow: "0 4px 18px rgba(15,23,42,.045)"
  },

  cardTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.94rem",
    fontWeight: 850
  },

  cardSubtitle: {
    marginTop: 4,
    color: "#94A3B8",
    fontSize: "0.72rem",
    lineHeight: 1.5
  },

  planGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 16,
    marginTop: 18
  },

  planCard: {
    padding: 18,
    borderRadius: 16,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#E8EDF5"
    }`
  },

  planHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12
  },

  planName: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "1rem",
    fontWeight: 900
  },

  planPrice: {
    marginTop: 5,
    color: "#1D4ED8",
    fontSize: "1.2rem",
    fontWeight: 900
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: "0.64rem",
    fontWeight: 850
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginTop: 16
  },

  statBox: {
    padding: 11,
    borderRadius: 11,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.05)"
        : "#E8EDF5"
    }`
  },

  statValue: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.92rem",
    fontWeight: 900
  },

  statLabel: {
    marginTop: 3,
    color: "#94A3B8",
    fontSize: "0.62rem"
  },

  featureGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(2, minmax(0, 1fr))",
    gap: 8,
    marginTop: 16
  },

  feature: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    borderRadius: 10,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.02)"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.045)"
        : "#EEF2F7"
    }`,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569",
    fontSize: "0.7rem",
    fontWeight: 700
  },

  packageGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 14,
    marginTop: 16
  },

  packageCard: {
    padding: 16,
    borderRadius: 14,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#E8EDF5"
    }`
  },

  empty: {
    padding: 30,
    textAlign: "center",
    color: "#94A3B8",
    fontSize: "0.76rem"
  }
}));

const formatNumber = value =>
  Number(value || 0).toLocaleString("pt-BR");

const formatCurrency = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const featureLabels = {
  whatsapp: "WhatsApp",
  facebook: "Facebook",
  instagram: "Instagram",
  campaigns: "Campanhas",
  schedules: "Agendamentos",
  internalChat: "Chat interno",
  externalApi: "API externa",
  kanban: "Kanban",
  openAi: "Korax IA",
  integrations: "Integrações",
  agenda: "Agenda",
  asaasRecurrence: "Recorrência Asaas"
};

const DashboardMasterPlans = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuper =
    user?.super === true ||
    user?.profile === "super";

  const loadPlans = useCallback(async () => {
    if (!isSuper) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await api.get(
        "/dashboard-master/plans"
      );

      setData(response.data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [isSuper]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const summary = data?.summary || {};
  const plans = data?.plans || [];
  const packages = data?.creditPackages || [];

  return (
    <KoraxPage className={classes.root}>
      <div className={classes.inner}>
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.button}
            onClick={() =>
              history.push("/dashboard-master")
            }
          >
            ← Voltar ao Dashboard Master
          </button>

          <button
            type="button"
            className={classes.button}
            onClick={loadPlans}
            disabled={loading}
          >
            {loading
              ? "Atualizando..."
              : "↻ Atualizar planos"}
          </button>
        </div>

        <KoraxPageHeader
          title="Gestão de Planos"
          subtitle="Controle central dos limites, recursos, empresas e pacotes da Korax."
        />

        <div className={classes.summaryGrid}>
          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Planos
            </div>
            <div className={classes.summaryValue}>
              {formatNumber(summary.totalPlans)}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Empresas
            </div>
            <div className={classes.summaryValue}>
              {formatNumber(summary.totalCompanies)}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Receita estimada
            </div>
            <div className={classes.summaryValue}>
              {formatCurrency(
                summary.estimatedMonthlyRevenue
              )}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Usuários em uso
            </div>
            <div className={classes.summaryValue}>
              {formatNumber(
                summary.totalConfiguredUsers
              )}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Conexões em uso
            </div>
            <div className={classes.summaryValue}>
              {formatNumber(
                summary.totalConfiguredConnections
              )}
            </div>
          </div>

          <div className={classes.summaryCard}>
            <div className={classes.summaryLabel}>
              Pacotes ativos
            </div>
            <div className={classes.summaryValue}>
              {formatNumber(
                summary.activeCreditPackages
              )}
            </div>
          </div>
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>
            Planos da plataforma
          </div>

          <div className={classes.cardSubtitle}>
            Cada plano define limites e módulos liberados para as empresas.
          </div>

          {loading && !data ? (
            <div className={classes.empty}>
              Carregando planos...
            </div>
          ) : plans.length === 0 ? (
            <div className={classes.empty}>
              Nenhum plano encontrado.
            </div>
          ) : (
            <div className={classes.planGrid}>
              {plans.map(plan => (
                <div
                  key={plan.id}
                  className={classes.planCard}
                >
                  <div className={classes.planHeader}>
                    <div>
                      <div className={classes.planName}>
                        {plan.name}
                      </div>

                      <div className={classes.planPrice}>
                        {formatCurrency(
                          plan.pricing.amount
                        )}
                      </div>
                    </div>

                    <span
                      className={classes.badge}
                      style={{
                        color:
                          plan.visibility.isPublic
                            ? "#15803D"
                            : "#64748B",
                        background:
                          plan.visibility.isPublic
                            ? "rgba(34,197,94,.11)"
                            : "rgba(100,116,139,.11)"
                      }}
                    >
                      {plan.visibility.isPublic
                        ? "Público"
                        : "Privado"}
                    </span>
                  </div>

                  <div className={classes.statsGrid}>
                    <div className={classes.statBox}>
                      <div className={classes.statValue}>
                        {formatNumber(
                          plan.usage.companies
                        )}
                      </div>
                      <div className={classes.statLabel}>
                        Empresas
                      </div>
                    </div>

                    <div className={classes.statBox}>
                      <div className={classes.statValue}>
                        {formatNumber(
                          plan.usage.users
                        )}
                        /
                        {formatNumber(
                          plan.limits.users
                        )}
                      </div>
                      <div className={classes.statLabel}>
                        Usuários usados/limite
                      </div>
                    </div>

                    <div className={classes.statBox}>
                      <div className={classes.statValue}>
                        {formatNumber(
                          plan.usage.connections
                        )}
                        /
                        {formatNumber(
                          plan.limits.connections
                        )}
                      </div>
                      <div className={classes.statLabel}>
                        Conexões usadas/limite
                      </div>
                    </div>
                  </div>

                  <div className={classes.featureGrid}>
                    {Object.entries(
                      featureLabels
                    ).map(([key, label]) => (
                      <div
                        key={key}
                        className={classes.feature}
                      >
                        <span>{label}</span>

                        <strong
                          style={{
                            color: plan.features[key]
                              ? "#15803D"
                              : "#B91C1C"
                          }}
                        >
                          {plan.features[key]
                            ? "Ativo"
                            : "Inativo"}
                        </strong>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className={classes.button}
                    style={{
                      width: "100%",
                      marginTop: 16
                    }}
                    onClick={() =>
                      history.push(
                        `/dashboard-master/plans/${plan.id}`
                      )
                    }
                  >
                    Editar plano
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>
            Pacotes de créditos
          </div>

          <div className={classes.cardSubtitle}>
            Pacotes adicionais disponíveis para compra.
          </div>

          <div className={classes.packageGrid}>
            {packages.map(item => (
              <div
                key={item.id}
                className={classes.packageCard}
              >
                <div className={classes.planName}>
                  {item.name}
                </div>

                <div className={classes.planPrice}>
                  {formatCurrency(item.price)}
                </div>

                <div
                  style={{
                    marginTop: 8,
                    color: "#64748B",
                    fontSize: "0.75rem"
                  }}
                >
                  {formatNumber(item.credits)} créditos
                </div>

                <div
                  style={{
                    marginTop: 10
                  }}
                >
                  <span
                    className={classes.badge}
                    style={{
                      color: item.active
                        ? "#15803D"
                        : "#64748B",
                      background: item.active
                        ? "rgba(34,197,94,.11)"
                        : "rgba(100,116,139,.11)"
                    }}
                  >
                    {item.active
                      ? "Ativo"
                      : "Inativo"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </KoraxPage>
  );
};

export default DashboardMasterPlans;
